import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { submittalsApi, projectsApi, type Submittal, type SubmittalDecideOutcome } from '@/lib/apiClient';
import { StateView, Badge, Modal, Button, ActivityTimeline, SelectWithOther } from '@/components';
import type { BadgeTone, StateViewQuery } from '@/components';
import { fmtDate } from '@/lib/dates';
import { reviewDueStatus } from './submittalStatus';
import { useCanEditProject } from './OverviewEdit';
import SubmittalEmailModal from './SubmittalEmailModal';
import ContactOrRoleSelect, { type PartyValue } from './contacts/ContactOrRoleSelect';
import { useJobContacts, contactLine } from './contacts/jobContacts';
import { describeRecipients } from './contacts/RecipientsField';
import type { EmailRecipient } from '@/lib/apiClient';
import SubmittalEditModal from './SubmittalEditModal';
import SubmittalRoundsCard from './SubmittalRoundsCard';
import SubmittalRevisionModal from './SubmittalRevisionModal';
import MarkSentModal from './MarkSentModal';
import { fmtSentAt, saveThenRefresh, toEasternInputValue } from './lib/sentAt';
import { canMarkNotSent, latestRound, nextRevision, revisionDueYmd, revisionLabel } from './submittalRounds';
import { SUBMITTAL_TYPE_OPTIONS, SUBMITTAL_REVIEWER_OPTIONS } from './submittalEdit';
import { saveBlob } from '../contracts/saveBlob';

// Type and Reviewer options (with an "Other…" escape hatch via SelectWithOther)
// live in submittalEdit.ts, shared with the edit form.
const BALL_OPTIONS = ['Reviewer', 'E&W', 'Subcontractor', '—'];

// Project identity needed for the email subject/body + Job Folder uploads.
// Fetched once at the tab root and threaded down to the create modal + detail.
type SubmittalProject = { jobNumber: string; title: string; jobFolderUrl: string | null };

// Whether a submittal's review-due deadline is "live" (i.e. worth coloring /
// surfacing a reminder for). Approved/closed/queued submittals aren't in review.
function isInReview(sub: Submittal): boolean {
  return sub.status === 'in-review' || sub.status === 'resubmit';
}

/**
 * "Did you send it?" for a submittal (#626). Opening the Outlook draft records nothing:
 * the app only builds a `mailto:` link and cannot see whether the email went. "Not sent
 * yet" leaves the submittal as it was, with Send with Outlook still on it.
 *
 * `revision` is the round a Send Revision would open (null for a first send, or when the
 * rounds are not known); confirming then also opens it.
 */
function ConfirmSubmittalSent({
  sub, revision, recipients = [], onSubmit, onClose,
}: {
  sub: Pick<Submittal, 'submittalNumber' | 'title' | 'createdAt'>;
  revision: number | null;
  /** Who the draft was addressed to; the caller records them with the send (#598 step 2). */
  recipients?: EmailRecipient[];
  onSubmit: (sentAt: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const name = sub.submittalNumber || sub.title;
  return (
    <MarkSentModal
      title="Did you send it?"
      intro={
        <>
          Mark <strong>{revision === null ? name : `${revisionLabel(revision)} of ${name}`}</strong> as
          sent once the email has gone from Outlook. That puts the submittal in review with the ball on
          the reviewer and starts the review clock from the time you give. If it hasn&apos;t gone yet,
          choose <strong>Not sent yet</strong> — you can send it from the submittal later.
          {recipients.length > 0 && <> It will be recorded as sent to {describeRecipients(recipients)}.</>}
        </>
      }
      min={toEasternInputValue(sub.createdAt)}
      confirmLabel="Mark as sent"
      cancelLabel="Not sent yet"
      onSubmit={onSubmit}
      onClose={onClose}
    />
  );
}

// Map a review-due tone to a text color for the list cell.
const DUE_COLOR: Record<BadgeTone, string | undefined> = {
  danger: 'var(--ew-danger)',
  warning: '#92400E',
  neutral: undefined,
  success: undefined,
  info: undefined,
};

// ─── Status → badge tone ────────────────────────────────────────────────────
// queued → neutral, in-review → info, resubmit → warning, approved → success,
// closed → neutral.
//
// ⚠️ 'closed' is a DEAD STATE as of 2026-07-25: no code path in
// submittals.service.ts ever sets it — a submittal's terminal state is
// 'approved' (or 'resubmit' pending a revision). The handling here and in
// STATE_IDX/WORKFLOW_STEPS below is kept deliberately, so a legacy or
// hand-edited row carrying 'closed' still renders sensibly instead of falling
// through to the raw string. Don't build on it, and don't be surprised that
// nothing produces it. (Product owner's call: leave the branch, document it.)
function statusBadge(sub: Submittal): { label: string; tone: BadgeTone } {
  switch (sub.status) {
    case 'in-review': return { label: 'In Review', tone: 'info' };
    case 'approved':  return { label: 'Approved',  tone: 'success' };
    case 'resubmit':  return { label: 'Resubmit',  tone: 'warning' };
    case 'queued':    return { label: 'Queued',    tone: 'neutral' };
    case 'closed':    return { label: 'Closed',    tone: 'neutral' };
    default:          return { label: sub.status,  tone: 'neutral' };
  }
}

// ─── Review-workflow stepper ─────────────────────────────────────────────────
const WORKFLOW_STEPS = ['Received from sub', 'In review', 'Returned', 'Closed'];
// Map a submittal status to its stepper index. 'closed' is never produced by
// the service (see the note above); if a legacy row carries it, it's treated
// like approved (final) = 3.
const STATE_IDX: Record<string, number> = {
  queued: 0,
  'in-review': 1,
  resubmit: 2,
  approved: 3,
  closed: 3,
};

// ─── Filter chips ───────────────────────────────────────────────────────────
type FilterKey = 'all' | 'overdue' | 'in-review' | 'approved' | 'resubmit' | 'queued';
const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'in-review', label: 'In Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'resubmit', label: 'Resubmit' },
  { key: 'queued', label: 'Queued' },
];

// Overdue = actively in review (in-review | resubmit) AND past its review-due date.
function isOverdue(sub: Submittal): boolean {
  if (!isInReview(sub)) return false;
  const { daysUntil } = reviewDueStatus(sub.reviewDue);
  return daysUntil != null && daysUntil < 0;
}

function matchesFilter(sub: Submittal, filter: FilterKey): boolean {
  if (filter === 'all') return true;
  if (filter === 'overdue') return isOverdue(sub);
  return sub.status === filter;
}

const css = `
  .sub-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .sub-table th {
    text-align: left; padding: 10px var(--ew-s-4); white-space: nowrap;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .sub-table td { padding: 11px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: top; }
  .sub-table tr:last-child td { border-bottom: 0; }
  .sub-row { cursor: pointer; transition: background var(--ew-dur-fast) var(--ew-ease); }
  .sub-row:hover { background: var(--ew-n-50); }
  .sub-num { font-variant-numeric: tabular-nums; font-weight: var(--ew-fw-medium); white-space: nowrap; }
  .sub-muted { color: var(--ew-n-500); }
  .sub-field { display: flex; flex-direction: column; }
  .sub-label { font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); margin-bottom: 5px; }
  .sub-input {
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 7px 10px;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); width: 100%;
    background: #fff; color: var(--ew-dark);
  }
  .sub-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: var(--ew-s-3); }

  /* Filter chips */
  .sub-chips { display: flex; gap: var(--ew-s-2); flex-wrap: wrap; }
  .sub-chip {
    background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-pill);
    padding: 5px 12px; cursor: pointer; color: var(--ew-n-600);
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
    transition: all var(--ew-dur-fast) var(--ew-ease);
  }
  .sub-chip:hover { border-color: var(--ew-n-300); color: var(--ew-dark); }
  .sub-chip.active { background: var(--ew-dark); border-color: var(--ew-dark); color: #fff; }
  .sub-chip-count {
    margin-left: 6px; font-variant-numeric: tabular-nums; font-size: 11px;
    color: var(--ew-n-400); font-weight: var(--ew-fw-semibold);
  }
  .sub-chip.active .sub-chip-count { color: rgba(255,255,255,0.7); }

  /* Review-workflow stepper */
  .sub-step { display: flex; align-items: center; gap: var(--ew-s-3); padding: 9px 0; }
  .sub-step-dot { width: 12px; height: 12px; border-radius: 50%; flex: 0 0 auto; }
  .sub-step-label { flex: 1; font: var(--ew-fw-medium) var(--ew-fs-body-sm) var(--ew-font); }
  .sub-step-tag {
    font: var(--ew-fw-semibold) 10px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase;
  }

  /* Decision buttons — full-width stack */
  .sub-decide-btns { display: grid; gap: var(--ew-s-2); margin-top: var(--ew-s-3); }
  .sub-decide-btns .btn { width: 100%; justify-content: center; }
  .sub-btn-danger { border-color: var(--ew-danger) !important; color: var(--ew-danger) !important; }

  /* Detail */
  .sub-back {
    background: transparent; border: 0; cursor: pointer; padding: 0;
    color: var(--ew-n-500); font: var(--ew-fw-medium) var(--ew-fs-body-sm) var(--ew-font);
    transition: color var(--ew-dur-fast) var(--ew-ease);
  }
  .sub-back:hover { color: var(--ew-dark); }
  .sub-section { padding: var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); }
  .sub-section:last-child { border-bottom: 0; }
  .sub-section-label { font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); margin: 0 0 var(--ew-s-2); }
  .sub-detail-row { display: flex; justify-content: space-between; gap: var(--ew-s-4); padding: 9px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .sub-detail-row:last-child { border-bottom: 0; }
  .sub-detail-row span:first-child { color: var(--ew-n-500); }
  .sub-detail-row span:last-child { color: var(--ew-dark); font-weight: var(--ew-fw-medium); text-align: right; }
  .sub-help { font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-500); }
  .sub-linkbtn {
    background: transparent; border: 0; cursor: pointer; padding: 0;
    color: var(--ew-quality); font: var(--ew-fw-medium) 12px/1.4 var(--ew-font);
    text-decoration: underline; text-underline-offset: 2px;
  }
  .sub-mono { font-family: var(--ew-font-mono, ui-monospace, monospace); background: var(--ew-n-100); border-radius: var(--ew-r-sm); padding: 2px 6px; color: var(--ew-n-700); word-break: break-all; }
`;

// Today as YYYY-MM-DD (local) for the Received date default.
function todayInput(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// Suggest the next submittal number for the create form: take the highest
// trailing integer across the project's existing submittal numbers and
// increment, preserving the prefix + zero-padding it already uses ("SUB-007" →
// "SUB-008"). Defaults to "SUB-001" when there are none. This only PRE-FILLS —
// the field stays editable, so numbering stays consistent by default without
// being forced. Mirrors nextRfiNumber in RfisTab.
function nextSubmittalNumber(submittals: Submittal[]): string {
  let best: { n: number; prefix: string; width: number } | null = null;
  for (const s of submittals) {
    const m = /^(.*?)(\d+)\s*$/.exec(s.submittalNumber ?? '');
    if (!m) continue;
    const n = parseInt(m[2], 10);
    if (!best || n > best.n) best = { n, prefix: m[1], width: m[2].length };
  }
  if (!best) return 'SUB-001';
  return best.prefix + String(best.n + 1).padStart(best.width, '0');
}

const EMPTY_FORM = {
  submittalNumber: '',
  title: '',
  specSection: '',
  submittalType: '',
  receivedAt: '',
  reviewDue: '',
};
const newForm = (): typeof EMPTY_FORM => ({ ...EMPTY_FORM, receivedAt: todayInput() });

export default function SubmittalsTab({ projectId, locked = false }: { projectId: number; locked?: boolean }) {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('sub');

  const query = useQuery({
    queryKey: ['submittals', projectId],
    queryFn: () => submittalsApi.list({ projectId }),
  });

  // Project identity for email subjects/bodies. Shares the workspace cache so
  // it's usually already warm; the composer falls back gracefully if not.
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<{ jobNumber: string; title: string; jobFolderUrl: string | null }>,
    enabled: Number.isFinite(projectId),
    staleTime: 60_000,
  });
  const project: SubmittalProject = {
    jobNumber: projectQuery.data?.jobNumber ?? '',
    title: projectQuery.data?.title ?? '',
    jobFolderUrl: projectQuery.data?.jobFolderUrl ?? null,
  };

  // ?sub=<id> → detail; keep ?tab=submittals intact when toggling.
  const openDetail = (id: number) => {
    const next = new URLSearchParams(params);
    next.set('tab', 'submittals');
    next.set('sub', String(id));
    setParams(next, { replace: true });
  };
  const backToList = () => {
    const next = new URLSearchParams(params);
    next.delete('sub');
    setParams(next, { replace: true });
  };

  if (selectedId) {
    return (
      <>
        <style>{css}</style>
        <SubmittalDetail projectId={projectId} submittalId={Number(selectedId)} project={project} onBack={backToList} locked={locked} />
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <SubmittalList projectId={projectId} query={query} project={project} onOpen={openDetail} locked={locked} />
    </>
  );
}

// ─── List view (filter chips + create modal) ────────────────────────────────
function SubmittalList({
  projectId,
  query,
  project,
  onOpen,
  locked = false,
}: {
  projectId: number;
  query: StateViewQuery<Submittal[]>;
  project: SubmittalProject;
  onOpen: (id: number) => void;
  locked?: boolean;
}) {
  const canEdit = useCanEditProject();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<typeof EMPTY_FORM>(newForm);
  const [filter, setFilter] = useState<FilterKey>('all');
  // After "Send with Outlook" creates the submittal, hold it so the composer can
  // open prefilled from what the PM typed.
  const [composeFor, setComposeFor] = useState<Submittal | null>(null);
  // The submittal whose draft was used, to ask "did you send it?" about (#626), and who
  // the draft was addressed to (#598 step 2).
  const [confirmFor, setConfirmFor] = useState<{ sub: Submittal; recipients: EmailRecipient[] } | null>(null);
  // "Reviewer": a job contact, or a role label as before (#598 step 2).
  const NO_REVIEWER: PartyValue = { contactId: null, label: '' };
  const [reviewer, setReviewer] = useState<PartyValue>(NO_REVIEWER);
  const { options: jobContacts, isLoading: contactsLoading } = useJobContacts(projectId, open);

  const set = (k: keyof typeof EMPTY_FORM) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));
  // For SelectWithOther, which yields a raw string value.
  const setVal = (k: keyof typeof EMPTY_FORM) => (v: string) =>
    setForm((p) => ({ ...p, [k]: v }));

  const openModal = () => {
    setReviewer(NO_REVIEWER);
    // Fresh form: Received defaulted to today + the next sequential submittal #
    // (editable) so numbering stays consistent by default.
    setForm({ ...newForm(), submittalNumber: nextSubmittalNumber(query.data ?? []) });
    setOpen(true);
  };
  const closeModal = () => {
    setReviewer(NO_REVIEWER);
    setOpen(false);
    setForm(newForm());
  };

  // Create a queued submittal from the form (shared by both footer actions).
  const createQueued = () => {
    const ne = (v: string) => (v.trim() === '' ? undefined : v.trim());
    // New Submittal → status=queued. Ball-in-court defaults to E&W server-side.
    // The +14d review-due default is applied server-side when reviewDue is omitted.
    return submittalsApi.create({
      projectId,
      submittalNumber: ne(form.submittalNumber),
      title: form.title.trim(),
      specSection: ne(form.specSection),
      submittalType: ne(form.submittalType),
      // A picked contact's name is the server's to write (it can outrun the label's limit).
      ...(reviewer.contactId != null
        ? { reviewerContactId: reviewer.contactId }
        : { reviewer: ne(reviewer.label) }),
      receivedAt: ne(form.receivedAt),
      reviewDue: ne(form.reviewDue),
      status: 'queued',
    });
  };

  // "Save as Queued" — create only, no email.
  const createMutation = useMutation({
    mutationFn: createQueued,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['submittals', projectId] });
      closeModal();
    },
  });

  // "Send with Outlook" — create (queued) → open the composer in 'send' mode for the
  // created submittal, then invalidate + close the form.
  //
  // ⚠️ Nothing here sends or records a send (#626). The submittal is saved as Queued;
  // it moves into review only when the PM confirms the email went, after the draft.
  // It used to be sent first, which moved a submittal into review before its draft had
  // even opened.
  const sendMutation = useMutation({
    mutationFn: createQueued,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['submittals', projectId] });
      setComposeFor(created);
      closeModal();
    },
  });

  const canSubmit = form.title.trim() !== '';
  const busy = createMutation.isPending || sendMutation.isPending;

  // Per-filter counts off the loaded list (so chips show a count pill).
  const all = query.data ?? [];
  const countFor = (key: FilterKey) => all.filter((s) => matchesFilter(s, key)).length;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--ew-s-3)', marginBottom: 'var(--ew-s-4)', flexWrap: 'wrap' }}>
        <div className="sub-chips">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`sub-chip${f.key === filter ? ' active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              <span className="sub-chip-count">({countFor(f.key)})</span>
            </button>
          ))}
        </div>
        {locked || !canEdit ? null : <button className="btn btn-primary" onClick={openModal}>+ New Submittal</button>}
      </div>

      <div className="pw-card">
        <h2 className="pw-card-hd">Submittals</h2>
        <StateView query={query} empty="No submittals yet.">
          {(submittals) => {
            const filtered = submittals.filter((s) => matchesFilter(s, filter));
            if (filtered.length === 0) {
              return <p style={{ padding: 'var(--ew-s-4)', color: 'var(--ew-n-400)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', textAlign: 'center' }}>No submittals match this filter.</p>;
            }
            return (
              <div className="ew-table-scroll">
                <table className="sub-table ew-cards">
                  <thead>
                    <tr>
                      <th>Submittal #</th>
                      <th>Submittal</th>
                      <th>Spec</th>
                      <th>Reviewer</th>
                      <th>Ball in court</th>
                      <th>Review due</th>
                      <th>Days in review</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((sub) => {
                      const b = statusBadge(sub);
                      // Color the due date only for submittals actively in review.
                      const dueColor = isInReview(sub) ? DUE_COLOR[reviewDueStatus(sub.reviewDue).tone] : undefined;
                      return (
                        <tr key={sub.id} className="sub-row" onClick={() => onOpen(sub.id)}>
                          <td className="sub-num" data-label="Submittal #">{sub.submittalNumber ?? '—'}</td>
                          <td data-label="Submittal">{sub.title}</td>
                          <td className="sub-muted" data-label="Spec">{sub.specSection ?? '—'}</td>
                          <td className="sub-muted" data-label="Reviewer">{sub.reviewer ?? '—'}</td>
                          <td className="sub-muted" data-label="Ball in court">{sub.ballInCourt ?? '—'}</td>
                          <td
                            className={dueColor ? undefined : 'sub-muted'}
                            data-label="Review due"
                            style={{ whiteSpace: 'nowrap', color: dueColor, fontWeight: dueColor ? 'var(--ew-fw-medium)' : undefined }}
                          >
                            {fmtDate(sub.reviewDue)}
                          </td>
                          <td className="sub-num" data-label="Days in review">{sub.daysInReview ?? '—'}</td>
                          <td data-label="Status"><Badge label={b.label} tone={b.tone} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          }}
        </StateView>
      </div>

      <Modal
        isOpen={open}
        onClose={closeModal}
        title="New Submittal"
        footer={
          <>
            <Button variant="secondary" onClick={closeModal} disabled={busy}>Cancel</Button>
            <Button
              variant="secondary"
              onClick={() => createMutation.mutate()}
              disabled={!canSubmit || busy}
              isLoading={createMutation.isPending}
            >
              Save as Queued
            </Button>
            <Button
              onClick={() => sendMutation.mutate()}
              disabled={!canSubmit || busy}
              isLoading={sendMutation.isPending}
            >
              Send with Outlook
            </Button>
          </>
        }
      >
        {(createMutation.isError || sendMutation.isError) && (
          <div style={{ marginBottom: 'var(--ew-s-3)', padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
            {((createMutation.error ?? sendMutation.error) as Error).message}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ew-s-3)' }}>
          <div className="sub-grid2">
            <div className="sub-field">
              <label className="sub-label">Submittal #</label>
              <input className="sub-input" value={form.submittalNumber} onChange={set('submittalNumber')} placeholder="e.g. SUB-001" />
              <p className="sub-help" style={{ marginTop: 'var(--ew-s-2)' }}>Auto-numbered — edit if needed.</p>
            </div>
            <div className="sub-field">
              <label className="sub-label">Title</label>
              <input className="sub-input" value={form.title} onChange={set('title')} />
            </div>
          </div>
          <div className="sub-grid2">
            <div className="sub-field">
              <label className="sub-label">Spec section</label>
              <input className="sub-input" value={form.specSection} onChange={set('specSection')} placeholder="e.g. 03 30 00" />
            </div>
            <SelectWithOther
              label="Type"
              options={SUBMITTAL_TYPE_OPTIONS}
              value={form.submittalType}
              onChange={setVal('submittalType')}
              placeholder="Select type…"
            />
          </div>
          <ContactOrRoleSelect
            label="Reviewer"
            contacts={jobContacts}
            loading={contactsLoading}
            roles={SUBMITTAL_REVIEWER_OPTIONS}
            value={reviewer}
            onChange={setReviewer}
            placeholder="Select reviewer…"
          />
          <div className="sub-grid2">
            <div className="sub-field">
              <label className="sub-label">Received</label>
              <input className="sub-input" type="date" value={form.receivedAt} onChange={set('receivedAt')} />
              <p className="sub-help" style={{ marginTop: 'var(--ew-s-2)' }}>Date the submittal package was received from the subcontractor.</p>
            </div>
            <div className="sub-field">
              <label className="sub-label">Review due</label>
              <input className="sub-input" type="date" value={form.reviewDue} onChange={set('reviewDue')} />
              <p className="sub-help" style={{ marginTop: 'var(--ew-s-2)' }}>Leave blank to default to 14 days out.</p>
            </div>
          </div>

          {/* Where documents go. The Review rounds card lives on the detail view
              because the upload filename is built from the submittal number,
              which doesn't exist until this form is saved.

              ⚠️ Split on jobFolderUrl: with no linked folder the Review rounds
              card offers NO upload control, so the first branch would send them
              to a dead end. */}
          {project.jobFolderUrl ? (
            <p className="sub-help" style={{ marginTop: 'var(--ew-s-1)' }}>
              Documents are attached after saving — open the submittal from the list
              and use its Review rounds card. That keeps a record of the package on the
              submittal; reviewer emails still need the files attached in Outlook.
            </p>
          ) : (
            <p className="sub-help" style={{ marginTop: 'var(--ew-s-1)' }}>
              This project has no Job Folder linked, so documents can't be kept on
              the submittal record yet — link one under Projects → edit. Reviewer
              emails need the files attached in Outlook either way.
            </p>
          )}
        </div>
      </Modal>

      {/* After "Send with Outlook" created the submittal, draft the email. */}
      {composeFor && (
        <SubmittalEmailModal
          sub={composeFor}
          project={project}
          projectId={projectId}
          mode="send"
          onClose={(used, recipients) => {
            if (used) setConfirmFor({ sub: composeFor, recipients });
            setComposeFor(null);
          }}
        />
      )}
      {/* Only after the draft was used, and "Not sent yet" leaves it Queued. */}
      {confirmFor && (
        <ConfirmSubmittalSent
          sub={confirmFor.sub}
          revision={null}
          recipients={confirmFor.recipients}
          onSubmit={(sentAt) => saveThenRefresh(
            submittalsApi.sendToReviewer(confirmFor.sub.id, sentAt, confirmFor.recipients),
            () => qc.invalidateQueries({ queryKey: ['submittals', projectId] }),
          )}
          onClose={() => setConfirmFor(null)}
        />
      )}
    </>
  );
}

// ─── Detail view ─────────────────────────────────────────────────────────────
function SubmittalDetail({
  projectId,
  submittalId,
  project,
  onBack,
  locked = false,
}: {
  projectId: number;
  submittalId: number;
  project: SubmittalProject;
  onBack: () => void;
  locked?: boolean;
}) {
  // Activity-locked (SYSTAC / ClosedWon, no Sage number yet) is read-only regardless
  // of permission, as on the Equipment, Materials, Changes and Receiving tabs. One
  // flag, so it covers every control below that is gated on it: Edit details, the
  // decision, send and send-revision, ball in court, the review window, and the Job
  // Folder upload and remove.
  //
  // ⚠️ UI only. The server gates CREATING a submittal, not changing one, and cannot
  // simply be told to: ProjectStageGuard reads `:id` as a PROJECT id first, which
  // on /submittals/:id is a submittal id, so it would check the wrong project.
  const canEdit = useCanEditProject() && !locked;
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  // Which composer (if any) is open, and for which submittal snapshot. `revision` is
  // the round a revision draft is for, so the email can name it; `receivedAt` is the day
  // that revision's package came back, held until the send is confirmed.
  const [compose, setCompose] = useState<{
    mode: 'send' | 'reminder' | 'revision'; sub: Submittal; revision?: number; receivedAt?: string;
  } | null>(null);
  // The submittal being edited, as it stood when "Edit details" was clicked.
  const [editing, setEditing] = useState<Submittal | null>(null);
  // Send Revision asks for the received day first; this is that dialog being open, and
  // what it leads to: the Outlook draft, or (the email already went) the sent time.
  const [revisionStep, setRevisionStep] = useState<'draft' | 'record' | null>(null);
  // The "when did you send it?" dialog being open (#626), and for what: a first send, a
  // revision (whose received day was asked first), or a correction (which holds the sent
  // time it was OPENED on, so a save made over someone else's change is refused).
  const [sentDialog, setSentDialog] = useState<
    | { kind: 'send'; recipients?: EmailRecipient[] }
    | { kind: 'revision'; receivedAt: string; revision: number | null; quotedDue?: string; recipients?: EmailRecipient[] }
    | { kind: 'correct'; openedWith: string }
    | null
  >(null);

  const query = useQuery({
    queryKey: ['submittal', submittalId],
    queryFn: () => submittalsApi.get(submittalId),
  });

  // The review rounds. A request of its own (not part of the submittal's), so the
  // detail never depends on it: a failure here shows in the rounds card and nowhere else.
  const roundsQuery = useQuery({
    queryKey: ['submittal-rounds', submittalId],
    queryFn: () => submittalsApi.rounds(submittalId),
  });

  // Activity timeline (who-did-what-when). Short staleTime so it stays fresh
  // without refetching on every render; explicitly invalidated below after any
  // workflow action so a new event shows immediately.
  const activityQuery = useQuery({
    queryKey: ['submittal-activity', submittalId],
    queryFn: () => submittalsApi.activity(submittalId),
    staleTime: 30_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['submittals', projectId] });
    qc.invalidateQueries({ queryKey: ['submittal', submittalId] });
    qc.invalidateQueries({ queryKey: ['submittal-activity', submittalId] });
    qc.invalidateQueries({ queryKey: ['submittal-rounds', submittalId] });
  };

  // The revision a Send Revision would open, or null when the rounds are not known
  // (still loading, failed, or none recorded yet).
  const nextRev = roundsQuery.data && roundsQuery.data.length > 0 ? nextRevision(roundsQuery.data) : null;
  // A reminder or a revision starts addressed to whoever the last round went to, checked
  // against the job's contacts as they are now, and the draft takes its recipients once,
  // when it opens. So those two wait for the rounds and the contacts rather than open early
  // addressed to the reviewer alone, or to someone taken off the job since (review). A
  // failed read doesn't hold them: the draft then starts from what is known, in plain view
  // before it is sent.
  const { isLoading: contactsLoading } = useJobContacts(projectId);
  const roundsLoading = roundsQuery.isPending || contactsLoading;

  // ⚠️ Neither "Send with Outlook" nor "Send Revision" records anything when it is
  // clicked (#626). They open the Outlook draft, and the send is recorded when the PM
  // confirms the email went, with the time it went: that is what moves the submittal
  // into review, starts the clock and (for a revision) opens the round. They used to
  // write all of it first, so a draft left in someone's Drafts folder read as sent,
  // and none of it could be corrected.

  // After the received day: the Outlook draft, or (the email already went) straight to
  // "when did you send it?". The draft quotes the due date a revision sent today gets,
  // since the API sets the real one only when the send is confirmed.
  const continueRevision = (receivedAt: string) => {
    const mode = revisionStep;
    const sub = query.data;
    setRevisionStep(null);
    if (!sub) return;
    if (mode === 'record') {
      setSentDialog({ kind: 'revision', receivedAt, revision: nextRev });
      return;
    }
    setCompose({
      mode: 'revision',
      sub: { ...sub, reviewDue: revisionDueYmd() },
      revision: nextRev ?? undefined,
      receivedAt,
    });
  };

  // Reassign ball-in-court from the sidebar SelectWithOther (fires on change).
  const reassignMutation = useMutation({
    mutationFn: (ballInCourt: string) => submittalsApi.update(submittalId, { ballInCourt }),
    onSuccess: invalidate,
  });

  // Set/reset the "of N" review window from the sidebar. A number is a manual
  // override; null resets it so the effective window auto-derives from the dates.
  const windowMutation = useMutation({
    mutationFn: (days: number | null) => submittalsApi.setReviewWindow(submittalId, days),
    onSuccess: invalidate,
  });

  // The submittal on the company's Excel transmittal (#646). The API names the file the
  // house way; the fallback is only for a browser that was not allowed to read that header.
  const exportMutation = useMutation({
    mutationFn: () => submittalsApi.exportForm(submittalId),
    onSuccess: ({ blob, fileName }) => saveBlob(blob, fileName ?? `Submittal ${query.data?.submittalNumber ?? submittalId}.xlsx`),
  });

  // Log a review decision → POST :id/decide (maps outcome → status/ball/due/text).
  const decideMutation = useMutation({
    mutationFn: (outcome: SubmittalDecideOutcome) =>
      submittalsApi.decide(submittalId, { outcome, note: note.trim() || undefined }),
    onSuccess: () => {
      setNote('');
      invalidate();
    },
  });

  return (
    <>
      <button className="sub-back" onClick={onBack} style={{ marginBottom: 'var(--ew-s-4)' }}>← Back to Submittals</button>

      <StateView query={query} isEmpty={(d) => !d} empty="Submittal not found.">
        {(sub) => {
          const b = statusBadge(sub);
          const stateIdx = STATE_IDX[sub.status] ?? 0;
          const due = reviewDueStatus(sub.reviewDue);
          const dueColor = DUE_COLOR[due.tone];

          return (
            <div className="ew-two-col">
              {/* Left column — header + workflow stepper + decision */}
              <div style={{ display: 'grid', gap: 'var(--ew-s-4)' }}>
                <div className="pw-card">
                  <div className="sub-section">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-2)', flexWrap: 'wrap' }}>
                      <span className="sub-num" style={{ font: 'var(--ew-fw-semibold) 18px/1 var(--ew-font)' }}>{sub.submittalNumber ?? '—'}</span>
                      {sub.specSection ? <span className="sub-num" style={{ font: 'var(--ew-fw-semibold) 14px/1 var(--ew-font)', color: 'var(--ew-n-500)' }}>{sub.specSection}</span> : null}
                      <Badge label={b.label} tone={b.tone} />
                      {canEdit && (
                        <Button variant="secondary" size="sm" onClick={() => setEditing(sub)} style={{ marginLeft: 'auto' }}>
                          Edit details
                        </Button>
                      )}
                    </div>
                    <h1 style={{ font: 'var(--ew-fw-semibold) 22px/1.2 var(--ew-font)', letterSpacing: '-0.01em', margin: '10px 0 0' }}>{sub.title}</h1>
                  </div>

                  <div className="sub-section">
                    <p className="sub-section-label">Decision</p>
                    {sub.decision
                      ? <p style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body) var(--ew-font)', color: 'var(--ew-dark)' }}>{sub.decision}</p>
                      : <p style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body) var(--ew-font)', color: 'var(--ew-n-400)' }}>No decision logged yet.</p>}
                    {/* A revision keeps the last round's decision on the submittal while it is
                        back in review, so without this it reads as the verdict on a package
                        nobody has answered yet. */}
                    {sub.decision && sub.status === 'in-review' && sub.decidedAt == null && (
                      <p className="sub-help" style={{ marginTop: 'var(--ew-s-2)' }}>
                        From the previous round.{' '}
                        {roundsQuery.data && roundsQuery.data.length > 0
                          ? `${revisionLabel(latestRound(roundsQuery.data)!.revision)} is in review.`
                          : 'The current round is in review.'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Review workflow stepper */}
                <div className="pw-card">
                  <h2 className="pw-card-hd">Review workflow</h2>
                  <div className="sub-section" style={{ borderBottom: 0 }}>
                    {WORKFLOW_STEPS.map((label, i) => {
                      const done = i < stateIdx;
                      const current = i === stateIdx;
                      // done → green, current → quality (blue), future → neutral.
                      const dotColor = done ? 'var(--ew-green)' : current ? 'var(--ew-quality)' : 'var(--ew-n-200)';
                      const tagColor = done ? 'var(--ew-green)' : current ? 'var(--ew-quality)' : 'var(--ew-n-400)';
                      return (
                        <div key={label} className="sub-step">
                          <span className="sub-step-dot" style={{ background: dotColor }} />
                          <span className="sub-step-label" style={{ color: i > stateIdx ? 'var(--ew-n-400)' : 'var(--ew-dark)' }}>{label}</span>
                          {(done || current) && (
                            <span className="sub-step-tag" style={{ color: tagColor }}>{done ? 'done' : 'current'}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Review rounds: Rev 0, Rev 1…, each with its dates, outcome and files.
                    Replaces the Job Folder card, which was one flat list of files. */}
                <SubmittalRoundsCard
                  sub={sub}
                  project={project}
                  canEdit={canEdit}
                  query={roundsQuery}
                />

                {/* Log review decision */}
                {canEdit && (
                <div className="pw-card">
                  <h2 className="pw-card-hd">Log review decision</h2>
                  <div className="sub-section" style={{ borderBottom: 0 }}>
                    {decideMutation.isError && (
                      <div style={{ marginBottom: 'var(--ew-s-3)', padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
                        {(decideMutation.error as Error).message}
                      </div>
                    )}
                    <label className="sub-label">Reviewer comments (optional)</label>
                    <textarea
                      className="sub-input"
                      style={{ minHeight: 90, resize: 'vertical' }}
                      placeholder="e.g. See redlines on sheet 3."
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                    <div className="sub-decide-btns">
                      <Button
                        variant="primary"
                        onClick={() => decideMutation.mutate('approved')}
                        disabled={decideMutation.isPending}
                        isLoading={decideMutation.isPending && decideMutation.variables === 'approved'}
                      >
                        Approved
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => decideMutation.mutate('approved-as-noted')}
                        disabled={decideMutation.isPending}
                        isLoading={decideMutation.isPending && decideMutation.variables === 'approved-as-noted'}
                      >
                        Approved as Noted
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => decideMutation.mutate('revise-resubmit')}
                        disabled={decideMutation.isPending}
                        isLoading={decideMutation.isPending && decideMutation.variables === 'revise-resubmit'}
                      >
                        Revise &amp; Resubmit
                      </Button>
                      <Button
                        variant="secondary"
                        className="sub-btn-danger"
                        onClick={() => decideMutation.mutate('rejected')}
                        disabled={decideMutation.isPending}
                        isLoading={decideMutation.isPending && decideMutation.variables === 'rejected'}
                      >
                        Rejected
                      </Button>
                    </div>
                  </div>
                </div>
                )}

                {/* History — generic activity timeline (reused unchanged by RFIs later) */}
                <div className="pw-card">
                  <h2 className="pw-card-hd">History</h2>
                  <div className="sub-section" style={{ borderBottom: 0 }}>
                    <ActivityTimeline events={activityQuery.data ?? []} />
                  </div>
                </div>
              </div>

              {/* Right column — metadata + reviewer notification + SharePoint stub */}
              <div style={{ display: 'grid', gap: 'var(--ew-s-4)' }}>
                <div className="pw-card">
                  <h2 className="pw-card-hd">Details</h2>
                  <div className="sub-detail-row"><span>Status</span><span><Badge label={b.label} tone={b.tone} /></span></div>
                  <div className="sub-detail-row"><span>Type</span><span>{sub.submittalType ?? '—'}</span></div>
                  <div className="sub-detail-row" style={{ alignItems: 'flex-start' }}>
                    <span>Reviewer</span>
                    <span style={{ textAlign: 'right' }}>
                      {sub.reviewerContact ? contactLine(sub.reviewerContact) : (sub.reviewer ?? '—')}
                      {sub.reviewerContact?.email && (
                        <><br /><a href={`mailto:${sub.reviewerContact.email}`}>{sub.reviewerContact.email}</a></>
                      )}
                    </span>
                  </div>
                  <div className="sub-detail-row"><span>Spec section</span><span>{sub.specSection ?? '—'}</span></div>
                  <div className="sub-detail-row">
                    <span>Days in review</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {sub.daysInReview != null ? `${sub.daysInReview} of ` : '—'}
                      {/* Split on canEdit FIRST. The number lives in the <input> for an
                          editor, so a read-only reader has to be given it as text —
                          otherwise "3 of " is followed by nothing but "(auto)" and the
                          denominator vanishes. */}
                      {sub.daysInReview != null && (canEdit ? (
                        <>
                          <input
                            // key on the effective value so a reset-to-auto re-renders
                            // this uncontrolled input with the freshly derived window.
                            key={sub.reviewWindowDays}
                            className="sub-input"
                            type="number"
                            min={1}
                            max={365}
                            defaultValue={sub.reviewWindowDays}
                            onBlur={(e) => {
                              const n = Number(e.target.value);
                              if (Number.isFinite(n) && n > 0 && n !== sub.reviewWindowDays) windowMutation.mutate(n);
                            }}
                            style={{ width: 56, padding: '3px 6px', textAlign: 'center' }}
                            title={sub.reviewWindowManual == null ? 'Auto-derived from dates — type to override' : 'Manual review window (days)'}
                          />
                          {sub.reviewWindowManual == null ? (
                            <span style={{ color: 'var(--ew-n-400)', font: 'var(--ew-fw-regular) 11px/1 var(--ew-font)' }}>(auto)</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => windowMutation.mutate(null)}
                              disabled={windowMutation.isPending}
                              title="Reset to auto-derive from dates"
                              style={{
                                background: 'transparent', border: 0, padding: 0, cursor: 'pointer',
                                color: 'var(--ew-quality)', font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)',
                              }}
                            >
                              auto
                            </button>
                          )}
                        </>
                      ) : (
                        <span title={sub.reviewWindowManual == null ? 'Auto-derived from dates' : 'Manual review window (days)'}>
                          {sub.reviewWindowDays} days
                          {sub.reviewWindowManual == null && (
                            <span style={{ color: 'var(--ew-n-400)', font: 'var(--ew-fw-regular) 11px/1 var(--ew-font)', marginLeft: 4 }}>(auto)</span>
                          )}
                        </span>
                      ))}
                    </span>
                  </div>
                  <div className="sub-detail-row"><span>Received</span><span>{fmtDate(sub.receivedAt)}</span></div>
                  <div className="sub-detail-row">
                    <span>Review due</span>
                    <span style={{ color: dueColor, fontWeight: dueColor ? 'var(--ew-fw-semibold)' : undefined }}>{fmtDate(sub.reviewDue)}</span>
                  </div>
                  <div className="sub-detail-row" style={{ alignItems: 'center' }}>
                    <span>Ball in court</span>
                    <span style={{ minWidth: 150 }}>
                      {canEdit ? (
                        <SelectWithOther
                          options={BALL_OPTIONS}
                          value={sub.ballInCourt ?? ''}
                          onChange={(v) => { if (v && v !== sub.ballInCourt) reassignMutation.mutate(v); }}
                          placeholder="Unassigned"
                        />
                      ) : (
                        <span>{sub.ballInCourt ?? 'Unassigned'}</span>
                      )}
                    </span>
                  </div>
                  {reassignMutation.isError && (
                    <p className="sub-help" style={{ color: 'var(--ew-danger)', padding: '0 var(--ew-s-4) var(--ew-s-2)' }}>
                      {(reassignMutation.error as Error).message}
                    </p>
                  )}
                </div>

                {/* Reviewer notification — manual Outlook model (no auto-reminders) */}
                <div className="pw-card">
                  <h2 className="pw-card-hd">Reviewer notification</h2>
                  <div className="sub-section" style={{ borderBottom: 0 }}>
                    {/* The sent time is shown in Eastern with the zone named: it is what
                        was typed when the send was confirmed, and a date alone read a
                        9 PM send as the next day (fmtDate on a UTC instant). */}
                    <p className="sub-help" style={{ marginBottom: 'var(--ew-s-3)' }}>
                      {sub.notifiedAt
                        ? <span style={{ color: 'var(--ew-dark)' }}>✉ Reviewer notified {fmtSentAt(sub.notifiedAt)}</span>
                        : 'Not yet sent to reviewer.'}
                      {canEdit && sub.notifiedAt && (
                        <>
                          {' '}
                          <button
                            type="button"
                            className="sub-linkbtn"
                            aria-label="Edit the sent time"
                            onClick={() => setSentDialog({ kind: 'correct', openedWith: sub.notifiedAt! })}
                          >
                            Edit
                          </button>
                        </>
                      )}
                    </p>

                    {/* Status-aware action: resubmit → Send Revision; queued/un-notified
                        (and not yet answered) → Send; in-review → Re-send notice;
                        approved/closed → none. Mark as sent… is for an email that went
                        without the draft, or after "Not sent yet".

                        ⚠️ Resubmit is tested BEFORE un-notified. A submittal answered
                        before its send was confirmed has no sent time but IS waiting on
                        a revision; Send with Outlook on it is refused (its round has
                        come back), and it would hide the button that works. */}
                    {canEdit && sub.status === 'resubmit' ? (
                      <div style={{ display: 'flex', gap: 'var(--ew-s-2)', flexWrap: 'wrap' }}>
                        <Button variant="primary" onClick={() => setRevisionStep('draft')} disabled={roundsLoading}>
                          Send Revision
                        </Button>
                        <Button variant="secondary" onClick={() => setRevisionStep('record')}>
                          Mark as sent…
                        </Button>
                      </div>
                    ) : canEdit && (sub.status === 'queued' || !sub.notifiedAt) && sub.decidedAt == null ? (
                      <div style={{ display: 'flex', gap: 'var(--ew-s-2)', flexWrap: 'wrap' }}>
                        <Button variant="primary" onClick={() => setCompose({ mode: 'send', sub })}>
                          Send with Outlook
                        </Button>
                        <Button variant="secondary" onClick={() => setSentDialog({ kind: 'send' })}>
                          Mark as sent…
                        </Button>
                      </div>
                    ) : sub.status === 'in-review' ? (
                      <Button variant="secondary" onClick={() => setCompose({ mode: 'reminder', sub })} disabled={roundsLoading}>
                        Re-send notice
                      </Button>
                    ) : null}

                    {/* The company submittal transmittal, filled in (#646). A read, so it is
                        offered to everyone who can open the submittal, at any status and on a
                        locked job. */}
                    <div style={{ marginTop: 'var(--ew-s-3)' }}>
                      <Button
                        variant="secondary"
                        onClick={() => exportMutation.mutate()}
                        isLoading={exportMutation.isPending}
                      >
                        Export to Excel
                      </Button>
                      <p className="sub-help" style={{ marginTop: 'var(--ew-s-2)' }}>
                        The company submittal transmittal, filled in from this submittal. Boxes left yellow are for you to fill in by hand.
                      </p>
                      {exportMutation.isError && (
                        <p className="sub-help" style={{ color: 'var(--ew-danger)', marginTop: 'var(--ew-s-2)' }}>
                          {(exportMutation.error as Error).message}
                        </p>
                      )}
                    </div>

                    {isInReview(sub) && (
                      <div style={{ marginTop: 'var(--ew-s-3)' }}>
                        <Badge label={due.label} tone={due.tone} />
                      </div>
                    )}

                    <p className="sub-help" style={{ marginTop: 'var(--ew-s-3)', fontStyle: 'italic' }}>
                      Opens an Outlook draft — you send it, then confirm here that it went.
                    </p>
                  </div>
                </div>

              </div>
            </div>
          );
        }}
      </StateView>

      {editing && (
        <SubmittalEditModal sub={editing} onClose={() => setEditing(null)} onSaved={invalidate} />
      )}

      {/* Send Revision: which day the corrected package came back. Records nothing:
          it leads to the draft, or to the sent time, and the round opens on confirm. */}
      {revisionStep && (
        <SubmittalRevisionModal
          revision={nextRev}
          mode={revisionStep}
          onCancel={() => setRevisionStep(null)}
          onConfirm={continueRevision}
        />
      )}

      {/* Outlook draft composer — 'send', 'reminder' or 'revision'. Closing a send or a
          revision draft that was used asks "did you send it?" (#626). */}
      {compose && (
        <SubmittalEmailModal
          sub={{ ...compose.sub, revision: compose.revision }}
          project={project}
          projectId={projectId}
          mode={compose.mode}
          // A reminder or a revision goes to whoever the last round went to.
          previousRecipients={compose.mode === 'send' ? undefined
            : [...(roundsQuery.data ?? [])].reverse().find((r) => (r.sentTo?.length ?? 0) > 0)?.sentTo}
          onClose={(used, recipients) => {
            if (used && compose.mode === 'send') setSentDialog({ kind: 'send', recipients });
            if (used && compose.mode === 'revision' && compose.receivedAt) {
              // The due date the draft quoted goes with the confirmation, so the tracker keeps the
              // date the reviewer was told even if the send is confirmed across Eastern midnight.
              setSentDialog({
                kind: 'revision', receivedAt: compose.receivedAt, revision: compose.revision ?? null,
                quotedDue: compose.sub.reviewDue ?? undefined, recipients,
              });
            }
            setCompose(null);
          }}
        />
      )}

      {/* ⚠️ These are gated on the submittal being loaded, not on its notifiedAt. A
          refused save refetches, and if someone else cleared the send meanwhile, a
          notifiedAt gate would make the dialog vanish with its error unread. */}
      {sentDialog?.kind === 'send' && query.data && (
        <ConfirmSubmittalSent
          sub={query.data}
          revision={null}
          recipients={sentDialog.recipients}
          onSubmit={(sentAt) => saveThenRefresh(submittalsApi.sendToReviewer(submittalId, sentAt, sentDialog.recipients), invalidate)}
          onClose={() => setSentDialog(null)}
        />
      )}
      {sentDialog?.kind === 'revision' && query.data && (
        <ConfirmSubmittalSent
          sub={query.data}
          revision={sentDialog.revision}
          recipients={sentDialog.recipients}
          onSubmit={(sentAt) => saveThenRefresh(
            submittalsApi.sendRevision(submittalId, {
              receivedAt: sentDialog.receivedAt, sentAt, reviewDue: sentDialog.quotedDue,
              ...(sentDialog.recipients?.length ? { recipients: sentDialog.recipients } : {}),
            }),
            invalidate,
          )}
          onClose={() => setSentDialog(null)}
        />
      )}
      {sentDialog?.kind === 'correct' && query.data && (
        <MarkSentModal
          title="Correct the sent time"
          intro={
            <>
              When did <strong>{query.data.submittalNumber || query.data.title}</strong> actually go to the
              reviewer? {canMarkNotSent(query.data, roundsQuery.data) && 'If it was never sent, mark it as not sent. '}
              The change is kept in the submittal&apos;s history.
            </>
          }
          initialValue={toEasternInputValue(sentDialog.openedWith)}
          min={toEasternInputValue(query.data.createdAt)}
          // A submittal answered cannot have been sent after its answer; the API refuses that.
          max={query.data.decidedAt ? toEasternInputValue(query.data.decidedAt) : undefined}
          confirmLabel="Save"
          cancelLabel="Cancel"
          onSubmit={(sentAt) => saveThenRefresh(submittalsApi.correctSentAt(submittalId, sentAt, sentDialog.openedWith), invalidate)}
          // Only a first review with no decision can go back to Queued; see canMarkNotSent.
          onClear={canMarkNotSent(query.data, roundsQuery.data)
            ? () => saveThenRefresh(submittalsApi.correctSentAt(submittalId, null, sentDialog.openedWith), invalidate)
            : undefined}
          onClose={() => setSentDialog(null)}
        />
      )}
    </>
  );
}
